import React from "react";
//#region types
import { CashReconciliationFormTypes, RevenueReportingFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
//#endregion
//#region components
import { DashboardButtonGroup, FilterButton, LoadingWithLogo, PageLayouts, ReloadEvent } from "components/common";
import {
  CashReconciliationList,
  CreateCashReconciliationButton,
  useCashReconciliationPermissions,
  useCashReconciliationReportFilterDefinition,
} from "components/cashReconciliation";
import { RevenueReportList, useRevenueReportListFilterDefinition } from "components/revenueReporting";
import { Grid } from "@mui/material";
import { useCashReconciliationService, useRevenueReportingService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const CashReconciliationReport: React.FC = () => {
  const { reportToAPIFilter } = useCashReconciliationService();
  const revenueReportingService = useRevenueReportingService();
  const {
    canViewCashReconciliations,
    canCreateCashReconciliations,
    hasRevenueReportingTemplates,
    revenueReportingTemplatesLoading,
    revenueReportingEnabledForCompany,
  } = useCashReconciliationPermissions();
  const cashFilterDefinition = useCashReconciliationReportFilterDefinition();
  const revenueFilterDefinition = useRevenueReportListFilterDefinition();

  const [hasRevenueReports, setHasRevenueReports] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    let mounted = true;
    revenueReportingService
      .getReports({
        page: 0,
        numPerPage: 1,
        orderByDescending: true,
        thenByDescending: false,
        isAutoPaginating: false,
      })
      .then((response) => mounted && setHasRevenueReports((response.data.totalItems ?? 0) > 0))
      .catch(() => mounted && setHasRevenueReports(false));
    return () => {
      mounted = false;
    };
  }, []);

  const isNewFlow = revenueReportingEnabledForCompany || hasRevenueReports || hasRevenueReportingTemplates;

  const getButtons = (reload: () => void) => {
    let buttons: (React.ReactElement | typeof ACTION_SEPARATOR)[] = [];
    if (canCreateCashReconciliations()) {
      buttons = [<CreateCashReconciliationButton reloadList={reload} />, ACTION_SEPARATOR];
    }
    buttons = [...buttons, <FilterButton />];
    return buttons;
  };

  if (hasRevenueReports === undefined || revenueReportingTemplatesLoading) {
    return <LoadingWithLogo />;
  }

  if (isNewFlow) {
    return (
      <ReloadEvent
        render={(reloadKey, reload) => (
          <PageLayouts.DashboardLayout<RevenueReportingFormTypes.Filter>
            entityKey={0}
            hasPermissions={canViewCashReconciliations()}
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.Reports)}
            topRightButtons={<DashboardButtonGroup buttons={getButtons(reload)} />}
            initialFilter={{ hideLegacy: true }}
            filterValueFormatter={revenueFilterDefinition.filterValueFormatter}
            filterFields={revenueFilterDefinition.filterFormFields}
            filterQueryStateHandler={revenueFilterDefinition.filterQueryStateHandler}
            apiToReportFilterFormatter={revenueFilterDefinition.apiToReportFilterFormatter}
            reportToAPIFilterFormatter={revenueReportingService.reportToAPIFilter}
          >
            <Grid container item>
              <RevenueReportList reloadKey={reloadKey} />
            </Grid>
          </PageLayouts.DashboardLayout>
        )}
      />
    );
  }

  return (
    <ReloadEvent
      render={(reloadKey, reload) => (
        <PageLayouts.DashboardLayout<CashReconciliationFormTypes.Filter>
          entityKey={0}
          hasPermissions={canViewCashReconciliations()}
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.CashReconciliation.CashReconciliations)}
          topRightButtons={<DashboardButtonGroup buttons={getButtons(reload)} />}
          filterFields={cashFilterDefinition.filterFormFields}
          filterQueryStateHandler={cashFilterDefinition.filterQueryStateHandler}
          apiToReportFilterFormatter={cashFilterDefinition.apiToReportFilterFormatter}
          reportToAPIFilterFormatter={reportToAPIFilter}
          filterCategory={WintSuperkollAPIModelCustomFilters.FilterCategory.CashReconciliation}
        >
          <Grid container item>
            <CashReconciliationList reloadKey={reloadKey} />
          </Grid>
        </PageLayouts.DashboardLayout>
      )}
    />
  );
};

export default CashReconciliationReport;
